import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";

import { SelfServeNodes } from "../../../types";
import { damageMissing } from "../../instantAnswers/orders-damagedMissing";
import { failedDeliveryAttempt } from "../../instantAnswers/orders-failedDeliveryAttempt";
import { invoiceDetails } from "../../instantAnswers/orders-invoiceDetails";
import { payoutCalculation } from "../../instantAnswers/orders-payoutCalculation";
import { RequestCommissionChanges } from "../../instantAnswers/orders-requestCommissionChanges";
import { returnedToSender } from "../../instantAnswers/orders-returnedToSender";
import {
  orderDeliveredStateVisibilityFn,
  orderShippedOrDeliveredAndSOYOVisibilityFn,
} from "../../visibilityFunctions/orderStateVisibilityFn";

export const useOrderDeliveredBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    DamageMissingItemReportDeliveredState: {
      id: "DamageMissingItemReportDeliveredState",
      label: strictLocalize({
        defaultMessage: "Damaged or missing item report",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderDeliveredStateVisibilityFn,
      instantAnswer: damageMissing(strictLocalize),
    },
    PayoutCalculationDeliveredState: {
      id: "PayoutCalculationDeliveredState",
      label: strictLocalize({
        defaultMessage: "Payout calculation",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: payoutCalculation(strictLocalize),
      isNodeVisible: orderDeliveredStateVisibilityFn,
    },
    InvoiceDetailsDeliveredState: {
      id: "InvoiceDetailsDeliveredState",
      label: strictLocalize({
        defaultMessage: "Invoice details",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: invoiceDetails(strictLocalize),
      articles: [articles.InvoicesAndBilling.RECEIVE_ORDER_INVOICE],
      isNodeVisible: orderDeliveredStateVisibilityFn,
    },
    ProblemWithDeliveryDeliveredState: {
      id: "ProblemWithDeliveryDeliveredState",
      label: strictLocalize({
        defaultMessage: "Problem with delivery",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "FailedDeliveryAttemptDeliveredState",
        "ReturnToSenderDeliveredState",
      ],
      level: 2,
      isNodeVisible: orderDeliveredStateVisibilityFn,
    },
    FailedDeliveryAttemptDeliveredState: {
      id: "FailedDeliveryAttemptDeliveredState",
      label: strictLocalize({
        defaultMessage: "Failed delivery attempt",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: failedDeliveryAttempt(strictLocalize),
      isNodeVisible: orderShippedOrDeliveredAndSOYOVisibilityFn,
    },
    ReturnToSenderDeliveredState: {
      id: "ReturnToSenderDeliveredState",
      label: strictLocalize({
        defaultMessage: "Return to sender",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: returnedToSender(strictLocalize),
      articles: [articles.MyOrders.ORDER_RETURNED_TO_SENDER],
      isNodeVisible: orderDeliveredStateVisibilityFn,
    },
    RequestCommissionChangesDeliveredState: {
      id: "RequestCommissionChangesDeliveredState",
      label: strictLocalize({
        defaultMessage: "Request commission change",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderDeliveredStateVisibilityFn,
      instantAnswer: RequestCommissionChanges(strictLocalize),
      articles: [articles.FaireDirect.ZERO_COMMISSION],
    },
  };
};
